import { Suspense, lazy } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import AppShell from "@/layout/AppShell";
import DashboardLayout from "@/layout/DashboardLayout";
import PublicLayout from "@/layout/PublicLayout";
import ModuleGuestRoute from "@/router/ModuleGuestRoute";
import ModuleProtectedRoute from "@/router/ModuleProtectedRoute";
import { renderSharedPublicRoutes } from "@/router/SharedPublicRoutes";
import StripPrefixRedirect from "@/router/StripPrefixRedirect";

const AdminLoginPage = lazy(() => import("@/modules/portal/admins/pages/AdminLoginPage"));
const AdminLandingPage = lazy(() => import("@/modules/portal/admins/pages/AdminLandingPage"));
const AdminSearchAnalyticsPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminSearchAnalyticsPage"),
);
const AdminEmailTrackingPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminEmailTrackingPage"),
);
const AdminProviderCommunicationsPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminProviderCommunicationsPage"),
);
const AdminEmailTemplatesPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminEmailTemplatesPage"),
);
const AdminBillingReportsPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminBillingReportsPage"),
);
const AdminChargeRulesPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminChargeRulesPage"),
);
const AdminSubscriptionManagementPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminSubscriptionManagementPage"),
);
const AdminLeadsReportsPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminLeadsReportsPage"),
);
const AdminProviderAccessPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminProviderAccessPage"),
);
const AdminProviderOnboardingPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminProviderOnboardingPage"),
);
const AdminInsuranceVerificationPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminInsuranceVerificationPage"),
);
const AdminProviderGrowthPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminProviderGrowthPage"),
);
const AdminProviderDemoRequestsPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminProviderDemoRequestsPage"),
);
const AdminProviderSignupsPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminProviderSignupsPage"),
);
const AdminServiceIntegrationsPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminServiceIntegrationsPage"),
);
// const AdminLeadScoringPage = lazy(
//   () => import("@/modules/portal/admins/pages/AdminLeadScoringPage"),
// );
// const AdminLeadScoringTesterPage = lazy(
//   () => import("@/modules/portal/admins/pages/AdminLeadScoringTesterPage"),
// );
const AdminCommunicationCheckPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminCommunicationCheckPage"),
);
const AdminCacheManagementPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminCacheManagementPage"),
);
const AdminMfaConfigurationPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminMfaConfigurationPage"),
);
const AdminSupportTicketsPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminSupportTicketsPage"),
);
const AdminTimerJobsPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminTimerJobsPage"),
);
const AdminTimerJobDetailPage = lazy(
  () => import("@/modules/portal/admins/pages/AdminTimerJobDetailPage"),
);
const AdminRouter = () => (
  <Suspense fallback={<div className="page-container page-padding">Loading...</div>}>
    <Routes>
      <Route element={<AppShell />}>
        <Route element={<PublicLayout />}>
          {renderSharedPublicRoutes(false)}
        </Route>

        <Route
          element={
            <ModuleGuestRoute>
              <DashboardLayout />
            </ModuleGuestRoute>
          }
        >
          <Route path="/login" element={<AdminLoginPage />} />
          <Route path="/user/login" element={<Navigate to="/login" replace />} />
          <Route path="/provider/login" element={<Navigate to="/login" replace />} />
          <Route path="/providers/login" element={<Navigate to="/login" replace />} />
          <Route path="/admin/login" element={<Navigate to="/login" replace />} />
          <Route path="/admins/login" element={<Navigate to="/login" replace />} />
        </Route>

        <Route
          element={
            <ModuleProtectedRoute allowedRoles={["admin"]} loginRoute="/login">
              <DashboardLayout />
            </ModuleProtectedRoute>
          }
        >
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<AdminLandingPage />} />
          <Route path="/analytics/search-intelligence" element={<AdminSearchAnalyticsPage />} />
          <Route path="/email-tracking" element={<AdminEmailTrackingPage />} />
          <Route path="/provider-communications" element={<AdminProviderCommunicationsPage />} />
          <Route path="/email-templates" element={<AdminEmailTemplatesPage />} />
          <Route path="/billing" element={<AdminBillingReportsPage />} />
          <Route path="/billing/reports" element={<Navigate to="/billing" replace />} />
          <Route path="/charge-rules" element={<AdminChargeRulesPage />} />
          <Route path="/billing/charge-rules" element={<Navigate to="/charge-rules" replace />} />
          <Route path="/subscriptions" element={<AdminSubscriptionManagementPage />} />
          <Route path="/leads/reports" element={<AdminLeadsReportsPage />} />
          <Route path="/provider-onboarding" element={<AdminProviderOnboardingPage />} />
          <Route path="/insurance-verification" element={<AdminInsuranceVerificationPage />} />
          <Route path="/providers/onboarding" element={<Navigate to="/provider-onboarding" replace />} />
          <Route path="/provider-growth" element={<AdminProviderGrowthPage />} />
          <Route path="/providers/growth" element={<Navigate to="/provider-growth" replace />} />
          <Route path="/provider-access" element={<AdminProviderAccessPage />} />
          <Route path="/providers/access" element={<Navigate to="/provider-access" replace />} />
          <Route path="/provider-demo-requests" element={<AdminProviderDemoRequestsPage />} />
          <Route path="/providers/demo-requests" element={<Navigate to="/provider-demo-requests" replace />} />
          <Route path="/provider-signups" element={<AdminProviderSignupsPage />} />
          <Route path="/providers/signups" element={<Navigate to="/provider-signups" replace />} />
          <Route path="/service-integrations" element={<AdminServiceIntegrationsPage />} />
          <Route path="/settings/service-integrations" element={<Navigate to="/service-integrations" replace />} />
          <Route path="/settings/email-templates" element={<Navigate to="/email-templates" replace />} />
          {/* <Route path="/lead-scoring" element={<AdminLeadScoringPage />} /> */}
          <Route path="/settings/lead-scoring" element={<Navigate to="/lead-scoring" replace />} />
          {/* <Route path="/lead-scoring/tester" element={<AdminLeadScoringTesterPage />} /> */}
          <Route path="/settings/lead-scoring/tester" element={<Navigate to="/lead-scoring/tester" replace />} />
          <Route path="/cache" element={<AdminCacheManagementPage />} />
          <Route path="/settings/cache-management" element={<Navigate to="/cache" replace />} />
          <Route path="/mfa" element={<AdminMfaConfigurationPage />} />
          <Route path="/settings/mfa-configuration" element={<Navigate to="/mfa" replace />} />
          <Route path="/communication-check" element={<AdminCommunicationCheckPage />} />
          <Route path="/settings/communication-check" element={<Navigate to="/communication-check" replace />} />
          <Route path="/support" element={<AdminSupportTicketsPage />} />
          <Route path="/timer-jobs" element={<AdminTimerJobsPage />} />
          <Route path="/timer-jobs/:jobKey" element={<AdminTimerJobDetailPage />} />
          <Route path="/timer-jobs/:id" element={<AdminTimerJobDetailPage />} />
        </Route>

        <Route path="/admin/*" element={<StripPrefixRedirect prefix="/admin" fallbackTo="/dashboard" />} />
        <Route path="/admins/*" element={<StripPrefixRedirect prefix="/admins" fallbackTo="/dashboard" />} />

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
  </Suspense>
);

export default AdminRouter;
